/**
 * Authentication Service — purchase-web thin build over the canonical factory.
 *
 * AUTHSVC-CONSOLIDATION-001 (Phase 3): the axios husk that owned every tenant
 * auth HTTP op (login, MFA verify, cross-app SSO exchange, passkey HTTP
 * legs, password reset, token refresh, validation) is retired in favour of
 * `@puffin-core/auth-client` `createAuthService`. The factory owns ALL endpoint
 * wiring, response extraction, error handling, and token persistence; this file
 * only:
 *   1. builds the transport over purchase-web's dedicated `/api/v1/auth` client
 *      pair (`createApiClientTransport`),
 *   2. selects the purchase shape (`mfaSetupShape: 'throw'` and the tenant
 *      `refreshEndpoint` for `refreshTenantToken`),
 *   3. re-exports the SAME named surface PurchasePuffin's `createAuthContext`
 *      wiring + consumers (LoginPage, via login-shell's usePasskeyLogin) depend on.
 *
 * AUTHSVC-CONSOLIDATION-002: the catastrophic-silent context projections
 * (login/logout/validateToken/verifyMfa/sendMfaOtp/authenticateWithPasskey/
 * exchangeCrossAppToken) are NO LONGER hand-aliased — a typo
 * `verifyMfa: factory.verifyMfa` (the never-throws twin) would silently
 * "succeed" an MFA failure with no tokens. The service object SPREADS
 * `factory.contextSurface` (the tenant context legs).
 *
 * A TENANT service only (Q-L16-98, 2026-09-29): the DUAL mode and its platform
 * legs are retired — platform staff use the separate `apps/console` app.
 *
 * Surface mapping (factory method → purchase export):
 *   - loginForContext                   → login          (positional, stores tokens, {user, locations})
 *   - verifyMfaForContext               → verifyMfa      (positional, throws, stores tokens, {user, locations})
 *   - logoutTenant                      → logout
 *   - refreshToken                      → refreshTenantToken ({token})
 *   - validateTenantToken (network)     → validateToken
 *   - exchangeCrossAppToken / getCurrentUser / getLocations / getToken → same names
 *   - requestPasswordReset / clearAuthData → same names
 *   - getPasskeyRegisterOptions / registerPasskey / getPasskeyDiscoverableOptions
 *     / getPasskeys / deletePasskey → same names
 *   - authenticateWithPasskeyForContext → authenticateWithPasskey (stores tokens, {user, locations, accessToken})
 *
 * The discoverable passkey-login ORCHESTRATOR (browser ceremony + HTTP legs) is
 * NOT here — it is `@puffin-core/login-shell` `authenticateWithDiscoverablePasskey`
 * (run by `usePasskeyLogin` on the login page), composing this service's
 * `getPasskeyDiscoverableOptions` + `authenticateWithPasskey` HTTP legs with the
 * canonical `@puffin-core/mfa-ui` `WebAuthnService` ceremony (USER-FACING-GEMS L1).
 *
 * SETAUTHHEADER-RETIRE (2026-10-26; console's Ruling 35, finished fleet-wide): the `setAuthHeader` sync of the stored
 * bearer onto `axios.defaults` is gone. Every authed request already reads the token per request through
 * `@puffin-core/api-client`'s `getToken`, and the global copy only leaked the bearer into clients built after a sign-in.
 *
 * @module features/auth/services/authService
 */

import { createApiClientTransport, createAuthService } from '@puffin-core/auth-client';
import { translateAuthError } from '@puffin-core/login-shell';
import { authStorage, REFRESH_ENDPOINT } from './authStorage';
import { publicAuthClient, authedAuthClient } from '../../../core/api/auth/authClient';

/**
 * Canonical purchase-web auth-service SUPERSET — the factory object selected to the
 * purchase shape. `mfaSetupShape: 'throw'` keeps any MFA-setup ops positional +
 * throw; `refreshEndpoint` backs `refreshTenantToken`.
 */
const factory = createAuthService({
  http: createApiClientTransport({
    publicClient: publicAuthClient,
    authedClient: authedAuthClient,
  }),
  tokenStore: authStorage,
  errorTranslator: translateAuthError,
  mfaSetupShape: 'throw',
  refreshEndpoint: REFRESH_ENDPOINT,
});

/**
 * PurchasePuffin auth service — the canonical factory surface aliased to the
 * exact method names + signatures every consumer (and `createAuthContext`)
 * already imports. No raw HTTP here; the factory owns all of it.
 *
 * AUTHSVC-CONSOLIDATION-002: instead of hand-aliasing the catastrophic-silent
 * context projections (a typo `verifyMfa: factory.verifyMfa` — the never-throws
 * twin — would silently "succeed" an MFA failure with no tokens), spread the
 * factory's pre-aliased surface. `contextSurface` carries the tenant context
 * legs (login/logout/validateToken/verifyMfa/sendMfaOtp/authenticateWithPasskey/
 * exchangeCrossAppToken). The remaining keys below are the local
 * getters + passkey HTTP legs + the refresh/reset/clear
 * the factory does NOT pre-alias.
 */
export const authService = {
  ...factory.contextSurface,

  // Tenant access-token refresh ({token})
  refreshTenantToken: factory.refreshToken,

  // Local getters
  getCurrentUser: factory.getCurrentUser,
  getToken: factory.getToken,
  getLocations: factory.getLocations,

  // Password reset email
  requestPasswordReset: factory.requestPasswordReset,

  // Clear local auth state
  clearAuthData: factory.clearAuthData,

  // Passkey (WebAuthn) HTTP legs
  getPasskeyRegisterOptions: factory.getPasskeyRegisterOptions,
  registerPasskey: factory.registerPasskey,
  getPasskeyDiscoverableOptions: factory.getPasskeyDiscoverableOptions,
  getPasskeys: factory.getPasskeys,
  deletePasskey: factory.deletePasskey,

  // MFA setup (throw shape: positional args, throw on error, return payload) — the shared setup router's five
  // in-session operations purchase-api mounts at /v1/auth/mfa for the TENANT realm, driven by `@puffin-core/mfa-ui`'s
  // `<MfaSettingsCard>` on the Security page (TENANT-ID-ORPHAN-REPAIR-001 S6b card unit 4; planner's surface).
  initMfaSetup: factory.initMfaSetup,
  verifyMfaSetup: factory.verifyMfaSetup,
  regenerateBackupCodes: factory.regenerateBackupCodes,
  getMfaStatus: factory.getMfaStatus,
  disableMfa: factory.disableMfa,
};

/**
 * RAW cross-domain marketing-login surface (MKT-LOGIN-XDOMAIN-001, 2026-06-29).
 *
 * The factory's UN-projected `login` / `verifyMfa` / `sendMfaOtp` legs — the
 * exact shape `@puffin-core/login-shell` `createMarketingLoginAuth` expects:
 *   - `login({ email, password, subdomain, captchaToken })` → raw response data
 *     (`{ requiresMfa, mfaToken, ... }` OR `{ accessToken, refreshToken, user }`),
 *   - `verifyMfa({ code, method, mfaToken })` → never-throws result object,
 *   - `sendMfaOtp({ method, mfaToken })` → never-throws result object.
 *
 * Deliberately NOT the `authService.login` above — that one is the positional,
 * token-storing context projection, which would break on the object-argument
 * contract. These raw legs also do NOT
 * persist tokens; the marketing-login hook injects its own `storeAuthData`
 * (the cross-domain redirect owns session establishment on the tenant origin).
 */
export const marketingLoginAuthService = {
  login: factory.login,
  verifyMfa: factory.verifyMfa,
  sendMfaOtp: factory.sendMfaOtp,
};

export default authService;
